import { useEffect, useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib'

const NAV_SCROLL_THRESHOLD = 20

const NAV_ROUTES = [
  { to: '/' as const,              key: 'home'           },
  { to: '/programmes' as const,    key: 'trainings'      },
  { to: '/certifications' as const, key: 'certifications' },
  { to: '/about' as const,         key: 'about'          },
  { to: '/partners' as const,      key: 'partners'       },
  { to: '/contact' as const,       key: 'contact'        },
  { to: '/community' as const,     key: 'community'      },
]

type LandingNavProps = {
  solidNav?: boolean
}

export default function LandingNav({ solidNav = false }: LandingNavProps) {
  const { t, i18n } = useTranslation()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [navElevated, setNavElevated] = useState(solidNav)
  const [mobileOpen, setMobileOpen] = useState(false)

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en'
  const toggleLang = () => i18n.changeLanguage(currentLang === 'en' ? 'fr' : 'en')

  useEffect(() => {
    if (solidNav) return
    const onScroll = () => setNavElevated(window.scrollY > NAV_SCROLL_THRESHOLD)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [solidNav])

  const elevated = solidNav || navElevated

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ease-out',
        elevated || mobileOpen
          ? 'bg-[#09090f]/95 border-b border-white/[0.07] backdrop-blur-xl'
          : 'bg-transparent border-b border-transparent',
      )}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 sm:px-10">

        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center" aria-label={t('landingNav.logoAria')}>
          <img
            src="/skillsuplogo.png"
            alt=""
            className="h-9 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav links */}
        <div className="hidden items-center gap-0.5 font-headline text-sm md:flex">
          {NAV_ROUTES.map(({ to, key }) => {
            const active = pathname === to
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'px-4 py-2 transition-colors duration-200 inline-flex items-end',
                  active
                    ? 'text-white font-semibold underline underline-offset-4 decoration-[#7c3aed] decoration-2'
                    : 'text-white/55 hover:text-white',
                )}
              >
                {t(`landingNav.${key}`)}
              </Link>
            )
          })}
        </div>

        {/* CTAs + hamburger */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            className="hidden md:inline-flex items-center justify-center px-3 py-2 rounded-xl font-headline text-xs font-bold uppercase tracking-wider text-white/60 transition-all duration-200 hover:text-white hover:bg-white/[0.06]"
          >
            {currentLang === 'en' ? 'FR' : 'EN'}
          </button>
          <Link
            to="/login"
            className="hidden md:inline-flex items-center justify-center border border-white/50 px-5 py-2 rounded-xl font-headline text-xs font-bold uppercase tracking-wider text-white transition-all duration-200 hover:border-white/75 hover:bg-white/[0.06] active:scale-[0.97]"
          >
            {t('landingNav.login')}
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center bg-[#7c3aed] px-5 py-2 rounded-xl font-headline text-xs font-bold uppercase tracking-wider text-white transition-all duration-200 hover:bg-[#6d28d9] hover:shadow-[0_0_28px_rgba(124,58,237,0.65)] active:scale-[0.97]"
            style={{ boxShadow: '0 0 18px rgba(124,58,237,0.4)' }}
          >
            {t('landingNav.register')}
          </Link>

          {/* Hamburger — mobile only */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? t('landingNav.closeMenu') : t('landingNav.openMenu')}
            aria-expanded={mobileOpen}
            className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-[5px] rounded-lg transition-colors hover:bg-white/[0.06]"
          >
            <span
              className={cn(
                'block h-[2px] w-5 bg-white rounded-full transition-transform duration-300 origin-center',
                mobileOpen && 'translate-y-[7px] rotate-45',
              )}
            />
            <span
              className={cn(
                'block h-[2px] w-5 bg-white rounded-full transition-opacity duration-300',
                mobileOpen && 'opacity-0',
              )}
            />
            <span
              className={cn(
                'block h-[2px] w-5 bg-white rounded-full transition-transform duration-300 origin-center',
                mobileOpen && '-translate-y-[7px] -rotate-45',
              )}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          'md:hidden overflow-hidden transition-all duration-300 ease-out',
          mobileOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="flex flex-col px-6 pb-6 pt-2 gap-1">
          {NAV_ROUTES.map(({ to, key }) => {
            const active = pathname === to
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'px-4 py-3 rounded-xl font-headline text-sm transition-colors duration-200',
                  active
                    ? 'text-white font-semibold bg-white/[0.06] border-l-2 border-[#7c3aed]'
                    : 'text-white/60 hover:text-white hover:bg-white/[0.04]',
                )}
              >
                {t(`landingNav.${key}`)}
              </Link>
            )
          })}
          <div className="mt-3 flex items-center gap-2">
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="flex-1 flex items-center justify-center border border-white/50 px-5 py-2.5 rounded-xl font-headline text-xs font-bold uppercase tracking-wider text-white transition-all duration-200 hover:border-white/75 hover:bg-white/[0.06]"
            >
              {t('landingNav.login')}
            </Link>
            <button
              onClick={() => { toggleLang(); setMobileOpen(false) }}
              className="flex items-center justify-center border border-white/30 px-4 py-2.5 rounded-xl font-headline text-xs font-bold uppercase tracking-wider text-white/60 transition-all duration-200 hover:text-white hover:bg-white/[0.06]"
            >
              {currentLang === 'en' ? 'FR' : 'EN'}
            </button>
          </div>
        </div>
      </div>
    </nav>
  )
}
